package com.neurobs.ccabserver.transcriptreview.components.transcriptselection.components

import com.neurobs.ccab.casr.{AccountSubjectChoice, AccountSubjectTestTranscriptView, TestChoice}
import com.neurobs.ccab.db.TestData
import com.neurobs.react.semanticui.{UiButtonFactory, UiCheckboxFactory, UiDropdownFactory}
import com.neurobs.ccabserver.transcriptreview.states.{ErrorState, FilterType, TranscriptSelectionState, UserState}
import com.neurobs.js.ccab.{CASRServer, CCABServer, ServerContext}
import com.neurobs.js.util.ServerResponseUtil
import com.neurobs.react.{ListBox, ListBoxItem, StyleUtil}
import org.scalajs.dom.console
import slinky.core.{Component, FunctionalComponent}
import slinky.core.annotations.react
import slinky.web.html._
import slinky.core.facade.Hooks._
import slinky.core.facade.ReactElement

import scala.concurrent.ExecutionContext.Implicits.global

//@react class MyComponent extends Component {
//	type Props = Unit // no props
//	case class State(selection: Int)
//
//	def initialState = State(0)
//
//	def render = {
//		table(
//			style := StyleUtil.makeStyle(("height", "300px"), ("overflowY", "auto"), ("color", "black"), ("display", "block"), ("width", "100px"))
//		)(
//			tbody(
//				Range(1,100) map { i =>
//					tr(
//						td(
//							style := StyleUtil.makeStyle(("backgroundColor", if (i == state.selection) "red" else "white")),
//							onClick := { () => setState(State(i)) }
//						)("hello")
//					)
//				}
//			)
//		)
//	}
//}

@react
object ReviewedStatusCheckbox {

	case class Props()

	val component = FunctionalComponent[Props] { _ =>

		val transcriptSelectionState = TranscriptSelectionState.accessState

		UiCheckboxFactory.horizontalRadioCheckbox[Option[Boolean]](
			title = "Reviewed status",
			selections = List(None, Some(true), Some(false)),
			labelGenerator = {t => t match {
				case None => "All"
				case Some(reviewed) => if (reviewed) "Reviewed" else "Unreviewed"
			}},
			selectionHandler = {t => transcriptSelectionState.setReviewedStatus(t)},
			isSelected = {t => transcriptSelectionState.data.value.reviewedStatus == t}
		)
	}
}

@react
object PrimaryFilterCheckbox {

	case class Props()

	val component = FunctionalComponent[Props] { _ =>

		val transcriptSelectionState = TranscriptSelectionState.accessState

		UiCheckboxFactory.horizontalRadioCheckbox[FilterType.Value](
			title = "Primary filter",
			selections = List(FilterType.AccountSubject, FilterType.Test),
			labelGenerator = {t => if (t == FilterType.Test) "Test" else "Subject Reporting Id"},
			selectionHandler = {t => transcriptSelectionState.setPrimaryFilter(t)},
			isSelected = {t => transcriptSelectionState.data.value.primaryFilterType == t}
		)
	}
}

@react
object TestFilterListBox {

	case class Props(primaryFilter: Boolean)

	val component = FunctionalComponent[Props] { props =>

		val transcriptSelectionState = TranscriptSelectionState.accessState
		val server = ServerContext.accessState()
		implicit val errorState = ErrorState.accessState

		def loadTestTranscriptSelections() = {
			transcriptSelectionState.loadTestTranscriptSelections(server)
		}

		useEffect(() => {
			if (props.primaryFilter) {
				if (transcriptSelectionState.data.value.test.isDefined && transcriptSelectionState.data.value.study.isDefined) {
					loadTestTranscriptSelections()
				}
			}
		}, Vector(transcriptSelectionState.data.value.test, transcriptSelectionState.data.value.study))

		def selectTest(testChoice: TestChoice) = {
			if (testChoice != TestChoice.nullChoice) transcriptSelectionState.selectTestFilter(Some(testChoice))
		}

		def generateTestChoiceItem(testChoice: TestChoice): List[ReactElement] = {
			if (testChoice == TestChoice.nullChoice) List(
				span(key := "empty", "[none]")
			)
			else List(
				span(key := "name", className := s"dropdown-text")(testChoice.testName),
				br(key := "br"),
				span(key := "iden", className := s"dropdown-subtext")(testChoice.testIdentifier)
			)
		}

		val deselectButton = button(
			onClick := { () => transcriptSelectionState.selectTestFilter(None) },
			style := StyleUtil.makeStyle(("marginTop", "5px"), ("padding", "5px"))
		)("Deselect")

		val choices: Seq[TestChoice] = transcriptSelectionState.data.value.testChoices match {
			case None => Seq()
			case Some(list) =>
				if (list.isEmpty) Seq(TestChoice.nullChoice)
				else list
		}
		case class TestItem(item: TestChoice) extends ListBoxItem {
			def content(): Seq[ReactElement] = generateTestChoiceItem(item)
			def equal(rhs: ListBoxItem): Boolean = item == rhs.asInstanceOf[TestItem].item
			def select(): Unit = selectTest(item)
			def key(): String = item.testId.toString
		}

		div(
			ListBox(
				options = choices.map(TestItem(_)),
				selection = transcriptSelectionState.data.value.test.map(TestItem(_)),
			),
			if (transcriptSelectionState.data.value.test.isDefined) deselectButton else null
		)
	}
}

@react
object AccountSubjectFilterListBox {

	case class Props(primaryFilter: Boolean)

	val component = FunctionalComponent[Props] { props =>

		val transcriptSelectionState = TranscriptSelectionState.accessState
		val server = ServerContext.accessState()
		implicit val errorState = ErrorState.accessState

		def loadTestTranscriptSelections() = {
			transcriptSelectionState.loadTestTranscriptSelections(server)
		}

		useEffect(() => {
			if (props.primaryFilter) {
				if (transcriptSelectionState.data.value.accountSubject.isDefined && transcriptSelectionState.data.value.study.isDefined) {
					loadTestTranscriptSelections()
				}
			}
		}, Vector(transcriptSelectionState.data.value.accountSubject))

		def selectAccountSubject(accountSubjectChoice: AccountSubjectChoice) = {
			if (accountSubjectChoice != AccountSubjectChoice.nullChoice) transcriptSelectionState.selectAccountSubjectFilter(Some(accountSubjectChoice))
		}

		def generateAccountSubjectChoiceItem(accountSubjectChoice: AccountSubjectChoice): List[ReactElement] = {
			val reportingIdString = accountSubjectChoice.reportingId.toString
			if (accountSubjectChoice == AccountSubjectChoice.nullChoice) List(
				span(key := "empty")("[none]")
			)
			else List(
				span(key := "item", className := s"dropdown-subtext")(reportingIdString)
			)
		}

		val deselectButton = button(
			onClick := { () => transcriptSelectionState.selectAccountSubjectFilter(None) },
			style := StyleUtil.makeStyle(("marginTop", "5px"), ("padding", "5px"))
		)("Deselect")

		val choices: Seq[AccountSubjectChoice] = transcriptSelectionState.data.value.accountSubjectChoices match {
			case None => Seq()
			case Some(list) =>
				if (list.isEmpty) Seq(AccountSubjectChoice.nullChoice)
				else list
		}
		case class SubjectItem(item: AccountSubjectChoice) extends ListBoxItem {
			def content(): Seq[ReactElement] = generateAccountSubjectChoiceItem(item)
			def equal(rhs: ListBoxItem): Boolean = item == rhs.asInstanceOf[SubjectItem].item
			def select(): Unit = selectAccountSubject(item)
			def key(): String = item.accountSubjectId.toString
		}
		div(
			ListBox(
				options = choices.map(SubjectItem(_)),
				selection = transcriptSelectionState.data.value.accountSubject.map(SubjectItem(_)),
			),
			if (transcriptSelectionState.data.value.accountSubject.isDefined) deselectButton else null
		)
	}
}



